import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SectionLabel from "./SectionLabel";

export default function LegacySection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const portraitRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const handleMove = (e: React.MouseEvent<HTMLElement>) => {
    const el = sectionRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMouse({
      x: (e.clientX - r.left) / r.width,
      y: (e.clientY - r.top) / r.height,
    });
  };

  const handlePortraitMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = portraitRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 8, ry: px * 10 });
  };

  const dotDrift = (strength: number) => ({
    transform: `translate3d(${(mouse.x - 0.5) * strength}px, ${
      (mouse.y - 0.5) * strength
    }px, 0)`,
    transition: "transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)",
  });

  return (
    <section
      ref={sectionRef}
      onMouseMove={handleMove}
      className="relative w-full overflow-hidden"
      style={{
        background: "#000",
        padding: "60px 16px 100px",
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
      }}
    >
      {/* cursor spotlight */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `radial-gradient(500px circle at ${mouse.x * 100}% ${
            mouse.y * 100
          }%, rgba(37,99,235,0.08), transparent 55%)`,
          transition: "background 0.2s ease-out",
        }}
      />

      <div
        className="mx-auto flex flex-col lg:grid gap-12 lg:gap-16 items-start relative"
        style={{
          maxWidth: 1440,
          gridTemplateColumns: "1.05fr 1fr",
        }}
      >
        {/* Left: text */}
        <div
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(40px)",
            transition:
              "opacity 0.9s cubic-bezier(0.2, 0.8, 0.2, 1), transform 0.9s cubic-bezier(0.2, 0.8, 0.2, 1)",
          }}
        >
          <SectionLabel>Our services — full cycle</SectionLabel>

          <div
            className="mt-10"
            style={{
              fontSize: "clamp(20px, 1.6vw, 24px)",
              lineHeight: 1.4,
              letterSpacing: "-0.01em",
              color: "#8A8A8A",
              maxWidth: 620,
            }}
          >
            <RevealParagraph
              inView={inView}
              delay={0.15}
              paragraph={
                <>
                  <span style={{ color: "#ECECEC", fontWeight: 500 }}>
                    We guide projects through every stage
                  </span>{" "}
                  — from the first idea and market analysis to development, launch, promotion, and long-term support. Our team brings engineering and marketing together under one growth strategy, so every product is built to perform and built to be found.
                </>
              }
            />
            <RevealParagraph
              inView={inView}
              delay={0.35}
              paragraph={
                <>
                  Each project is powered by experienced project managers, developers, UI/UX designers, marketers, and QA engineers — a cross-functional team where every specialist shapes the final quality of the product.
                </>
              }
            />
            <RevealParagraph
              inView={inView}
              delay={0.55}
              paragraph={
                <>
                  We value speed, transparency, and efficiency — you always know how your project is progressing and what results your advertising delivers. It's a principle we hold with every client, on every project.
                </>
              }
            />
          </div>

          <div
            className="mt-10"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(20px)",
              transition:
                "opacity 0.8s ease 0.8s, transform 0.8s ease 0.8s",
            }}
          >
            <ReadStoryBtn />
          </div>
        </div>

        {/* Right: portrait */}
        <div className="relative w-full lg:w-auto" style={{ minHeight: 320 }}>
          <div
            ref={portraitRef}
            onMouseMove={handlePortraitMove}
            onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
            className="relative lg:absolute overflow-hidden cursor-pointer w-full lg:w-[520px] mx-auto lg:mx-0"
            style={{
              height: 320,
              borderRadius: 160,
              background: "#1a1a1a",
              perspective: 900,
              opacity: inView ? 1 : 0,
              transform: inView
                ? `translateY(0) scale(1)`
                : `translateY(60px) scale(0.9)`,
              transition:
                "opacity 1s cubic-bezier(0.2, 0.8, 0.2, 1) 0.2s, transform 1s cubic-bezier(0.2, 0.8, 0.2, 1) 0.2s",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
                transition: "transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
                transformStyle: "preserve-3d",
              }}
            >
              <img
                src="https://storage.readdy-site.link/project_files/c0e0600e-4e78-44ef-81fe-aa0be702df4c/5d0feb44-1961-42d8-b336-3150f200f0b9_compressed_ChatGPT-Image-13-.-2026-.-13_46_55.webp"
                alt="Intellivex team"
                className="w-full h-full"
                style={{ objectFit: "cover", objectPosition: "top" }}
              />
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.16) 50%, transparent 70%)",
                  transform: `translateX(${(mouse.x - 0.5) * 120}%)`,
                  transition: "transform 0.6s ease",
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>

          {/* Decorative dot cluster with mouse parallax */}
          <div
            className="absolute"
            style={{ left: 20, top: 40, width: 200, height: 200 }}
          >
            <span
              className="absolute rounded-full"
              style={{
                width: 60,
                height: 60,
                background: "#2563EB",
                left: 0,
                top: 40,
                ...dotDrift(30),
                animation: "legacyDot 5.5s ease-in-out infinite",
              }}
            />
            <span
              className="absolute rounded-full"
              style={{
                width: 30,
                height: 30,
                background: "#60A5FA",
                left: 70,
                top: 0,
                ...dotDrift(-40),
                animation: "legacyDot 6.5s ease-in-out infinite 0.6s",
              }}
            />
            <span
              className="absolute rounded-full"
              style={{
                width: 20,
                height: 20,
                background: "#93C5FD",
                left: 100,
                top: 90,
                ...dotDrift(50),
                animation: "legacyDot 4.8s ease-in-out infinite 1.2s",
              }}
            />
            <span
              className="absolute rounded-full"
              style={{
                width: 14,
                height: 14,
                background: "#8A8A8A",
                left: 40,
                top: 120,
                ...dotDrift(-25),
                animation: "legacyDot 7.2s ease-in-out infinite 0.3s",
              }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes legacyDot {
          0%, 100% { opacity: 0.9; }
          50%      { opacity: 0.55; }
        }
      `}</style>
    </section>
  );
}

function RevealParagraph({
  paragraph,
  inView,
  delay,
}: {
  paragraph: React.ReactNode;
  inView: boolean;
  delay: number;
}) {
  return (
    <p
      style={{
        margin: 0,
        marginBottom: 24,
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(16px)",
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
    >
      {paragraph}
    </p>
  );
}

function ReadStoryBtn() {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to="/our-services"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="whitespace-nowrap cursor-pointer relative overflow-hidden inline-flex items-center gap-3"
      style={{
        background: "transparent",
        color: hover ? "#000" : "#ECECEC",
        borderRadius: 9999,
        padding: "11px 22px",
        fontWeight: 600,
        fontSize: 14,
        border: hover ? "1px solid #ECECEC" : "1px solid rgba(255,255,255,0.55)",
        transition: "color 0.35s ease, border 0.35s ease",
        textDecoration: "none",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: "#ECECEC",
          borderRadius: 9999,
          transform: hover ? "scale(1)" : "scale(0)",
          transformOrigin: "left center",
          transition: "transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      />
      <span style={{ position: "relative" }}>Learn more</span>
      <span
        aria-hidden
        style={{
          position: "relative",
          display: "inline-flex",
          width: 16,
          height: 16,
          alignItems: "center",
          justifyContent: "center",
          transform: hover ? "translateX(4px)" : "translateX(0)",
          transition: "transform 0.35s ease",
        }}
      >
        <i className="ri-arrow-right-line" style={{ fontSize: 16 }} />
      </span>
    </Link>
  );
}